import { useState } from "react";
import classNames from "classnames";
import { XCircleIcon } from "@heroicons/react/20/solid";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import CiroButton, { CiroButtonStyleEnum } from "../../../shared/CiroButton";

interface ISpecificCompaniesDisplay {
  specificCompanies: string[];
  onRemoveCompany: (index: number) => void;
  onClearAll?: () => void;
}

const SpecificCompaniesDisplay = ({
  specificCompanies,
  onRemoveCompany,
  onClearAll,
}: ISpecificCompaniesDisplay) => {
  const [expanded, setExpanded] = useState(false);

  const initialDisplayCount = 5;

  const displayedCompanies = expanded
    ? specificCompanies
    : specificCompanies.slice(0, initialDisplayCount);

  const hasMoreToShow = specificCompanies.length > initialDisplayCount;

  const showClearButton = specificCompanies.length >= 3 && onClearAll;

  if (specificCompanies.length === 0) {
    return null;
  }

  return (
    <div className={classNames("bg-gray-50", "p-3", "rounded-md", "mt-3")}>
      <div
        className={classNames(
          "text-xs",
          "text-gray-500",
          "mb-2",
          "flex",
          "justify-between",
          "items-center",
        )}
      >
        <span>
          {specificCompanies.length}{" "}
          {specificCompanies.length === 1 ? "company" : "companies"} specified
        </span>

        <div className={classNames("flex", "items-center", "gap-3")}>
          {showClearButton && (
            <CiroButton
              style={CiroButtonStyleEnum.UNSTYLED}
              onClick={onClearAll}
              analyticsField="Clear all companies"
              customClassName={classNames(
                "text-xs",
                "text-gray-500 hover:text-gray-700",
                "flex",
                "items-center",
              )}
            >
              Clear all
            </CiroButton>
          )}

          {hasMoreToShow && (
            <CiroButton
              style={CiroButtonStyleEnum.UNSTYLED}
              onClick={() => setExpanded(!expanded)}
              analyticsField={
                expanded ? "Collapse company list" : "Expand company list"
              }
              customClassName={classNames(
                "text-xs",
                "text-orange-500 hover:text-orange-700",
                "flex",
                "items-center",
              )}
            >
              {expanded ? (
                <>
                  Show less
                  <ChevronUpIcon className={classNames("h-3", "w-3", "ml-1")} />
                </>
              ) : (
                <>
                  Show all {specificCompanies.length}
                  <ChevronDownIcon
                    className={classNames("h-3", "w-3", "ml-1")}
                  />
                </>
              )}
            </CiroButton>
          )}
        </div>
      </div>
      <div className={classNames("flex", "flex-wrap", "gap-2")}>
        {displayedCompanies.map((company, index) => (
          <div
            key={index}
            className={classNames(
              "flex",
              "items-center",
              "bg-white",
              "border",
              "border-gray-200",
              "rounded-full",
              "py-1",
              "px-3",
              "text-sm",
            )}
          >
            <span className={classNames("mr-1")}>{company}</span>
            <XCircleIcon
              className={classNames(
                "h-4",
                "w-4",
                "text-gray-400",
                "hover:text-gray-600",
                "cursor-pointer",
              )}
              onClick={() => onRemoveCompany(index)}
            />
          </div>
        ))}

        {!expanded && hasMoreToShow && (
          <div
            className={classNames(
              "flex",
              "items-center",
              "bg-gray-100",
              "text-gray-500",
              "border",
              "border-gray-200",
              "rounded-full",
              "py-1",
              "px-3",
              "text-sm",
            )}
          >
            +{specificCompanies.length - initialDisplayCount} more
          </div>
        )}
      </div>
    </div>
  );
};

export default SpecificCompaniesDisplay;
